import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Promise<void> | null} */
  #hotstopMobileCartSyncPromise = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHotStopMobileCartOpen);
    window.addEventListener('pageshow', this.#handleHotStopPageShow);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHotStopMobileCartOpen);
    window.removeEventListener('pageshow', this.#handleHotStopPageShow);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /**
   * HOTSTOP B90T — Mobile cart state synchronization.
   *
   * Multi-line custom additions can leave Horizon holding section HTML from
   * the original add response while /cart.js already contains the complete
   * cart. Mobile browsers can also restore a pre-login drawer from bfcache.
   * Refreshing the drawer's server-rendered header section on open makes the
   * visible line items, count, totals and automatic discounts authoritative.
   */
  #refreshHotStopMobileCart = async (reason = 'open') => {
    if (!isMobileBreakpoint()) return;
    if (this.#hotstopMobileCartSyncPromise) return this.#hotstopMobileCartSyncPromise;

    const dialog = this.refs.dialog;
    const cartItems = dialog?.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : '';
    if (!sectionId) return;

    document.documentElement.dataset.hotstopB90tCartSync = 'refreshing';
    document.documentElement.dataset.hotstopB90tCartSyncReason = reason;

    const url = new URL(window.location.href);
    url.searchParams.set('_hotstop_cart_sync', String(Date.now()));

    this.#hotstopMobileCartSyncPromise = sectionRenderer
      .renderSection(sectionId, { cache: false, mode: 'hydration', url })
      .then(() => {
        document.documentElement.dataset.hotstopB90tCartSync = 'synced';
        this.#updateStickyState();
      })
      .catch((error) => {
        console.warn('[HotStop B90T] Mobile cart section refresh failed.', error);
        document.documentElement.dataset.hotstopB90tCartSync = 'error';
      })
      .finally(() => {
        this.#hotstopMobileCartSyncPromise = null;
      });

    return this.#hotstopMobileCartSyncPromise;
  };

  #handleHotStopMobileCartOpen = () => {
    void this.#refreshHotStopMobileCart('drawer-open');
  };

  #handleHotStopPageShow = (event) => {
    if (!event.persisted || !isMobileBreakpoint()) return;
    void this.#refreshHotStopMobileCart('pageshow-bfcache');
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
